import { formatWholeNumber } from "@common/utils/i18n";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { myWalletQueryOptions } from "./me";
import { api } from "./utils";

export type TokenValuesMap = Record<string, { value: number; decimalPlaces: number }>;

export const tokenValuesQueryOptions = queryOptions({
  queryKey: ["token-values"],
  queryFn: async (): Promise<TokenValuesMap> => {
    const response = await api.data.getTokenValues();
    // Convert array to lookup map
    return Object.fromEntries(
      response.token_details.map(detail => [
        detail.token_currency,
        { value: detail.token_value.amount ?? 0, decimalPlaces: detail.decimal_places },
      ]),
    );
  },
  staleTime: 1000 * 60 * 60, // Cache for 1 hour since values don't change often
});

/**
 * Get the dollar value and formatting info for a given token amount and currency.
 * Returns undefined if the currency is not found or values are not loaded.
 */
export function getTokenDollarValue(
  amount: number,
  tokenCurrency: string,
  values: TokenValuesMap | undefined,
): { dollarValue: number; decimalPlaces: number } | undefined {
  if (!values) return undefined;
  const tokenDetails = values[tokenCurrency];
  if (!tokenDetails) return undefined;
  return {
    dollarValue: amount * tokenDetails.value,
    decimalPlaces: tokenDetails.decimalPlaces,
  };
}

/**
 * Convert a dollar amount back to points.
 * Returns undefined if the currency is not found or values are not loaded.
 */
export function getDollarTokenAmount(
  dollarAmount: number,
  tokenCurrency: string,
  values: TokenValuesMap | undefined,
): number | undefined {
  if (!values) return undefined;
  const tokenDetails = values[tokenCurrency];
  if (!tokenDetails || tokenDetails.value === 0) return undefined;
  return Math.round(dollarAmount / tokenDetails.value);
}

/**
 * Hook to get the formatted token amount string.
 * Returns the dollar value if available, otherwise returns "X points".
 * @param amount - The token amount to format
 * @param options.showFreeForZero - If true, returns "Free" for zero amounts (default: true). Set to false for balance displays.
 */
export function useFormattedTokenAmount(amount: number, options?: { showFreeForZero?: boolean }): string {
  const { showFreeForZero = true } = options ?? {};
  const walletQuery = useQuery(myWalletQueryOptions);
  const tokenValuesQuery = useQuery(tokenValuesQueryOptions);

  const tokenCurrency = walletQuery.data?.token_currency;
  const tokenValues = tokenValuesQuery.data;

  if (amount === 0 && showFreeForZero) return "Free";

  if (tokenCurrency) {
    const result = getTokenDollarValue(amount, tokenCurrency, tokenValues);
    if (result) {
      return result.dollarValue.toLocaleString(undefined, {
        minimumFractionDigits: result.decimalPlaces,
        maximumFractionDigits: result.decimalPlaces,
      });
    }
  }

  if (amount === 0) return "0";

  const unit = amount === 1 ? "point" : "points";
  return `${formatWholeNumber(amount)} ${unit}`;
}
